import { CustomRenderMethodInput, Map as MlMap, MercatorCoordinate } from "maplibre-gl";
import earcut, { flatten } from "earcut";
import { pmtilesElevAt } from "../terrain/terrain-pmtiles";
import { mapWithConcurrency } from "../terrain/route-elevation-profile";
import { lngLatElevToMercator, Obstacle3dLayer } from "./webgl-maplibre.obstacle-3d-layer";

type XY = number[];
interface Approach {
  sector: GeoJSON.Feature<GeoJSON.Polygon>;
  line: GeoJSON.Feature<GeoJSON.LineString>;
  label: GeoJSON.Feature<GeoJSON.Point>;
  path: XY[]; // Ground to IAP, independent of source winding.
  altitudeM: number;
}
const distance = (a: XY, b: XY) => {
  const lat = ((a[1] + b[1]) * Math.PI) / 360;
  return Math.hypot((a[0] - b[0]) * Math.cos(lat), a[1] - b[1]) * 111320;
};
function contains(ring: XY[], p: XY): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i],
      b = ring[j];
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

/** Continuous dash-dot phase across all vertices, in ground metres. */
export function dashDotPath(path: XY[], unit: number): XY[][] {
  if (!(unit > 0) || !Number.isFinite(unit)) return [];
  const pattern = [20, 10, 2, 10].map((n) => n * unit);
  const result: XY[][] = [];
  let phase = 0,
    remaining = pattern[0];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1],
      b = path[i],
      length = distance(a, b);
    if (!length) continue;
    let along = 0;
    const at = (d: number) => [a[0] + ((b[0] - a[0]) * d) / length, a[1] + ((b[1] - a[1]) * d) / length];
    while (along < length - 1e-8) {
      const step = Math.min(remaining, length - along);
      if (phase % 2 === 0) result.push([at(along), at(along + step)]);
      along += step;
      remaining -= step;
      if (remaining < 1e-8) {
        phase = (phase + 1) % pattern.length;
        remaining = pattern[phase];
      }
    }
  }
  return result;
}

/** Legacy navdata stores FDN polygon, centerline and IAP label as separate rows. */
export function findApproaches(features: GeoJSON.Feature[]): Approach[] {
  const runways = features.filter((f) => f.properties?._objType === 11);
  const labels = runways.filter(
    (f): f is GeoJSON.Feature<GeoJSON.Point> =>
      f.geometry.type === "Point" && /^IAP\s+\d+(?:\.\d+)?$/i.test(String(f.properties?.text).trim())
  );
  const lines = runways.filter(
    (f): f is GeoJSON.Feature<GeoJSON.LineString> =>
      f.geometry.type === "LineString" && Array.isArray(f.properties?.lineDash)
  );
  const result: Approach[] = [];
  for (const sector of runways) {
    if (sector.geometry.type !== "Polygon" || sector.properties?.heliportSector !== true) continue;
    const ring = sector.geometry.coordinates[0];
    const matches: Approach[] = [];
    for (const line of lines) {
      const coords = line.geometry.coordinates;
      if (coords.length < 2) continue;
      for (const label of labels) {
        const at = label.geometry.coordinates;
        const forward = distance(coords[coords.length - 1], at) < 3;
        if (!forward && distance(coords[0], at) >= 3) continue;
        const path = forward ? coords : [...coords].reverse();
        if (Math.min(...ring.map((p) => distance(p, path[0]))) > 40) continue;
        // Confirm the centerline actually enters this sector, not a neighbouring fan.
        const origin = path[0];
        const next = path.find((p) => distance(p, origin) > 1);
        if (!next) continue;
        const probe = [origin[0] + (next[0] - origin[0]) * 0.1, origin[1] + (next[1] - origin[1]) * 0.1];
        if (!contains(ring, probe)) continue;
        matches.push({
          sector: sector as GeoJSON.Feature<GeoJSON.Polygon>,
          line,
          label,
          path,
          altitudeM: Number(String(label.properties.text).trim().slice(3).trim()) * 0.3048,
        });
      }
    }
    // Ambiguous or incomplete data keeps its original 2D representation.
    if (matches.length === 1) result.push(matches[0]);
  }
  return result;
}

/** Interpolate altitude along the closest centerline segment, supporting curved approaches. */
export function approachAltitude(point: XY, path: XY[], groundM: number, iapM: number): number {
  const projected = path.map((p) => MercatorCoordinate.fromLngLat([p[0], p[1]]));
  const q = MercatorCoordinate.fromLngLat([point[0], point[1]]);
  let total = 0,
    bestDistance = Infinity,
    along = 0;
  for (let i = 1; i < projected.length; i++) {
    const a = projected[i - 1],
      b = projected[i];
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (!length) continue;
    const t = Math.max(0, Math.min(1, ((q.x - a.x) * dx + (q.y - a.y) * dy) / (length * length)));
    const gap = Math.hypot(q.x - a.x - t * dx, q.y - a.y - t * dy);
    if (gap < bestDistance) {
      bestDistance = gap;
      along = total + length * t;
    }
    total += length;
  }
  return total ? groundM + ((iapM - groundM) * along) / total : groundM;
}

class ApproachLayer extends Obstacle3dLayer {
  // `shown` is the last value written; every write to a label's style costs a
  // style recalculation, so the frame loop writes only what changed.
  labels: { element: HTMLDivElement; position: [number, number, number]; shown: string }[] = [];
  constructor(private readonly map: MlMap) {
    super("approach-sectors-3d", 1, 2, false);
  }
  clearLabels(): void {
    for (const label of this.labels) label.element.remove();
    this.labels = [];
  }
  addLabel(text: string, position: [number, number, number]): void {
    const element = document.createElement("div");
    element.textContent = text;
    Object.assign(element.style, {
      position: "absolute",
      left: "0",
      top: "0",
      display: "none",
      pointerEvents: "none",
      color: "#000",
      background: "#fff",
      border: "1px solid #000",
      padding: "1px 3px",
      font: "bold 13px sans-serif",
      whiteSpace: "nowrap",
    });
    this.map.getCanvasContainer().appendChild(element);
    this.labels.push({ element, position, shown: "none" });
  }
  render(gl: WebGLRenderingContext | WebGL2RenderingContext, input: CustomRenderMethodInput): void {
    if (this.map.getZoom() < (this.map.getLayer("vm-runway-labels")?.minzoom ?? 0)) {
      for (const label of this.labels) this.show(label, "none");
      return;
    }
    super.render(gl, input);
    const m = input.defaultProjectionData.mainMatrix;
    const canvas = this.map.getCanvas();
    for (const label of this.labels) {
      const [x, y, z] = label.position;
      const w = m[3] * x + m[7] * y + m[11] * z + m[15];
      const px = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
      const py = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
      const visible = w > 0 && Math.abs(px) <= 1 && Math.abs(py) <= 1;
      const left = Math.round(((px + 1) * canvas.clientWidth) / 2);
      const top = Math.round(((1 - py) * canvas.clientHeight) / 2);
      this.show(label, visible ? `translate(${left}px, ${top}px) translate(-50%, -120%)` : "none");
    }
  }
  private show(label: { element: HTMLDivElement; shown: string }, transform: string): void {
    if (label.shown === transform) return;
    if (transform === "none") label.element.style.display = "none";
    else {
      if (label.shown === "none") label.element.style.display = "block";
      label.element.style.transform = transform;
    }
    label.shown = transform;
  }
}

export class ApproachSectors {
  private layer: ApproachLayer;
  private controller = new AbortController();
  private sequence = 0;
  private pending = false;
  private rendered = new Set<string>();

  private data: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  constructor(
    private readonly map: MlMap,
    private readonly publish: (data: GeoJSON.FeatureCollection) => void
  ) {
    this.layer = new ApproachLayer(map);
    map.addLayer(this.layer);
    map.on("moveend", this.finishMove);
  }
  private featureKey(f: GeoJSON.Feature): string {
    return `${f.properties?._objType}:${f.id}`;
  }
  private finishMove = (): void => {
    if (this.pending) void this.setData(this.data);
  };
  async setData(data: GeoJSON.FeatureCollection): Promise<void> {
    this.data = data;
    const seq = ++this.sequence;
    this.controller.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    // Publish the chart now. The DEM lookups below can take seconds, and the
    // vectors must not wait for them. Sectors already drawn in 3D stay hidden.
    const early = data.features.filter((f) => !this.rendered.has(this.featureKey(f)));
    this.publish({ ...data, features: early });
    if (this.map.isMoving()) {
      this.pending = true;
      return;
    }
    this.pending = false;
    const approaches = findApproaches(data.features);

    const ground = await mapWithConcurrency(approaches, 4, async (a) => {
      try {
        return await pmtilesElevAt(a.path[0][0], a.path[0][1], signal);
      } catch {
        return null;
      }
    });
    if (seq !== this.sequence || signal.aborted) return;
    const triangles: number[] = [],
      lines: number[] = [],
      colors: number[] = [];
    const replaced = new Set<GeoJSON.Feature>();

    this.layer.clearLabels();
    approaches.forEach((a, i) => {
      if (ground[i] === null || !Number.isFinite(ground[i])) return;
      const altitude = (p: XY) => approachAltitude(p, a.path, ground[i], a.altitudeM);
      const rings = a.sector.geometry.coordinates;
      const flat = flatten(rings);
      const indices = earcut(flat.vertices, flat.holes, flat.dimensions);
      const coords = rings.flat();
      const vertices = coords.map((p) => lngLatElevToMercator(p[0], p[1], altitude(p)));
      for (const index of indices) triangles.push(...vertices[index], 0.475, 0.475, 0.475, 0.35);
      const addLine = (path: XY[], color: number[]) => {
        for (let j = 1; j < path.length; j++) {
          const p = path[j - 1],
            q = path[j];
          lines.push(
            ...lngLatElevToMercator(p[0], p[1], altitude(p)),
            ...lngLatElevToMercator(q[0], q[1], altitude(q))
          );
          colors.push(...color);
        }
      };
      for (const ring of rings) addLine(ring, [0.475, 0.475, 0.475, 1]);
      // Dark dash-dot marks remain legible on the pale sector/runway surface.
      const metresPerPixel = (78271.51696 * Math.cos((a.path[0][1] * Math.PI) / 180)) / Math.pow(2, this.map.getZoom());
      for (const dash of dashDotPath(a.path, Math.max(0.25, metresPerPixel))) addLine(dash, [0, 0, 0, 1]);
      const at = a.label.geometry.coordinates;
      this.layer.addLabel(String(a.label.properties.text), lngLatElevToMercator(at[0], at[1], a.altitudeM));
      replaced.add(a.sector);
      replaced.add(a.line);
      replaced.add(a.label);
    });
    this.layer.setData(
      new Float64Array(lines),
      new Float64Array(0),
      new Float64Array(0),
      new Float64Array(triangles),
      new Float32Array(colors)
    );
    this.rendered = new Set([...replaced].filter((f) => f.id !== undefined).map((f) => this.featureKey(f)));
    const final = data.features.filter((f) => !replaced.has(f));
    // Same features as the early publish: skip a second parse of the whole chart.
    if (final.length !== early.length || final.some((f, i) => f !== early[i])) {
      this.publish({ ...data, features: final });
    }
    this.map.triggerRepaint();
  }
  remove(): void {
    this.sequence++;
    this.controller.abort();
    this.map.off("moveend", this.finishMove);
    this.layer.clearLabels();
    if (this.map.getLayer(this.layer.id)) this.map.removeLayer(this.layer.id);
    this.publish(this.data);
  }
}
